import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Event Item Component
const EventItem: React.FC<{ image: string; index: number }> = ({ image, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl transition-all duration-500 hover:shadow-2xl hover:shadow-brand-orange/20 hover:-translate-y-2 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        transitionDelay: `${index * 100}ms`
      }}
    >
      {/* Image Container with Events Effect */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        {/* Main event image */}
        <div className="absolute inset-0 transform group-hover:scale-105 transition-transform duration-700">
          <img 
            src={image} 
            alt="Event Showcase"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Events Overlay Effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎉</span>
              <h3 className="text-lg sm:text-xl font-bold">Event</h3>
            </div>
            <p className="text-sm sm:text-base opacity-90 mb-2">Spectacular event and memorable brand experience</p>
            <div className="flex justify-between items-center text-xs sm:text-sm">
              <span className="bg-gradient-to-r from-brand-orange to-brand-gold px-3 py-1 rounded-full font-semibold">Events</span>
              <span className="opacity-75">2024</span>
            </div>
          </div>
        </div>

        {/* Events Floating Elements */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:animate-bounce">
          <span className="text-white text-sm">EV</span>
        </div>
        <div className="absolute top-4 left-4 w-6 h-6 bg-brand-orange/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:animate-pulse"></div>
      </div>
    </div>
  );
};

const EventsPage: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Event images from the Events folder
  const eventImages = [
    '/Events/27b26844-d199-47d3-88a6-5a8daccd5955.jpg',
    '/Events/307fd131-de4a-44a1-b7bb-70e5b3726baf.jpg',
    '/Events/32182631-2bab-4531-8b56-751f122e6b96.jpg',
    '/Events/38ef9123-c6f0-4f4e-ade3-4d9166436827.jpg',
    '/Events/39d23f1f-6bfe-41f2-995c-bebfeb5af62c.jpg',
    '/Events/3b313c6f-8757-4610-b964-79f86fc36d94.jpg',
    '/Events/45b05020-8935-445a-a838-ec8e08751427.jpg',
    '/Events/4637d1a2-111e-47d6-aac7-0025cb84d460.jpg',
    '/Events/5b311946-ca1c-4354-8afd-9f146ae12a92.jpg',
    '/Events/5b5ac1cd-d46e-4084-9b50-23e778141739.jpg',
    '/Events/5da392bc-87b7-4a7e-aa29-2bc131ed2bc4.jpg',
    '/Events/626fe02a-497f-4bfc-a10c-90208d45560d.jpg',
    '/Events/778e6dad-77e5-44b3-a540-37d27a11f595.jpg',
    '/Events/7970ae41-8af7-41ab-a4a0-743a3d6b0f59.jpg',
    '/Events/84a5f4c7-920d-453d-bf31-e7df669161b1.jpg',
    '/Events/8e446838-a4a2-473b-b370-86064b884e51.jpg',
    '/Events/95dae8ce-894b-4a3b-9160-5b4eed601fa7.jpg',
    '/Events/a3ef765c-9573-4416-b52d-4de60a9f81d3.jpg',
    '/Events/a41bfb0a-84de-4d62-bee0-24301385d6fc.jpg',

    '/Events/b1379f06-3f99-4929-989a-0eaee4275739.jpg',
    '/Events/c03aa4bf-d933-4698-966e-1404e702eac0.jpg',
    '/Events/d52e855f-2879-4bf1-a43f-07cd438c76b5.jpg',
    '/Events/d7494945-8943-4a04-860e-cac38e26d869.jpg',
    '/Events/dc86895f-1885-46db-83ac-8c40b6d46212.jpg',
    '/Events/e22534ab-cfb6-437c-8f07-c243b0ec412f.jpg',
    '/Events/e509e2c3-9292-4cf6-b29c-1f6bfd78597c.jpg',
    '/Events/ed7b1e3b-a191-4c9a-9454-02b116ce1a51.jpg',
    '/Events/f31977d6-743d-474f-bda9-83f30aa2eb65.jpg',
    '/Events/f86f5a6a-17b3-466d-8cd2-288911352bfd.jpg',
    '/Events/IMG_1051.JPG',
    '/Events/IMG_1052.JPG',
    '/Events/IMG_1053.JPG',
    '/Events/IMG_1054.JPG',
    '/Events/IMG_1055.JPG',
    '/Events/IMG_1056.JPG',
    '/Events/IMG_1057.JPG',
    '/Events/IMG_1058.JPG'
  ];

  const stats = [
    { number: '40+', label: 'Events', icon: '🎉' },
    { number: '100%', label: 'Success', icon: '✨' },
    { number: '24/7', label: 'Support', icon: '🚀' }
  ];

  return (
    <div className="pt-32 min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-yellow-600/10"></div>
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            ref={headerRef}
            className={`text-center transition-all duration-1000 ${
              headerVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span>Brand Experiences</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Events
              </span>
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Gallery
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Explore our spectacular events, brand activations, and memorable experiences that create lasting impressions and drive engagement.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm sm:text-base text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-gray-900 via-brand-primary to-gray-900 bg-clip-text text-transparent">
                Events Gallery
              </span>
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-4"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From spectacular brand activations to memorable experiences, discover how our events create lasting impressions and drive engagement.
            </p>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {eventImages.map((image, index) => (
              <EventItem key={index} image={image} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-primary to-brand-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Create an Unforgettable Event?
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Let's create a spectacular event experience that captures attention and drives your brand forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/quote"
              className="bg-white text-brand-orange px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Get Quote</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-brand-orange transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Back to Home</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
